import React, { useState, useEffect, useRef, useMemo } from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import { connect } from 'react-redux';
import { useHistory, useLocation } from "react-router-dom";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import { SUPER_ADMIN_EMAIL, ROLE_SUB_ADMIN } from '../constants';
import { fetchUserByEmail } from 'DBServices/firestoreDBService';
import useAnimatedNavToggler from "../helpers/useAnimatedNavToggler.js";

import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { ReactComponent as SearchIcon } from "images/misc/search.svg";
import Header2 from 'components/headers/Header.js'
import SearchResultsDropdown from './SearchResultsDropdown';

// Build a flat, searchable list of foods / markets / categories from the
// marketplace Redux state.
const buildSearchIndex = (data) => {
  const tabs = data?.tabs || {};
  const foods = (tabs.Foods || []).map((f) => ({ ...f, type: "food" }));
  const markets = (tabs.Market || []).map((m) => ({ ...m, type: "market" }));
  const categories = (tabs.Category || []).map((c) => ({ ...c, type: "category" }));
  return [...foods, ...markets, ...categories];
};

const Header = styled.header`
  ${tw`flex justify-between items-center -mx-8 h-auto my-0 py-4 px-8 text-white shadow-md`}
  background: linear-gradient(90deg, #022c22 0%, #064e3b 50%, #022c22 100%);
  border-bottom: 1px solid rgba(6, 78, 59, 0.4);
`;

export const NavLinks = tw.div`inline-block`;

/* hocus: stands for "on hover or focus"
 * hocus:bg-primary-700 will apply the bg-primary-700 class on hover or focus
 */
export const NavLink = styled.a`
  ${tw`text-lg my-0 lg:text-sm lg:mx-6 lg:my-0 font-semibold tracking-wide transition duration-300 pb-1 border-b-2 border-transparent cursor-pointer`}
  color: #d1fae5;
  &:hover, &:focus {
    color: #fbbf24;
    border-color: #fbbf24;
  }
  ${({ selected }) => selected && `
    color: #fbbf24;
    border-color: #fbbf24;
  `}
`;

export const PrimaryLink = styled(NavLink)`
  ${tw`lg:mx-0 px-8 py-3 rounded-full font-bold shadow-md focus:shadow-outline transform transition duration-200`}
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: #0f172a !important;
  border-bottom: 0;

  &:hover, &:focus {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    color: #020617 !important;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }
`;

export const LogoLink = styled(NavLink)`
  ${tw`flex items-center border-b-0 text-2xl! ml-0! lg:ml-4! xl:ml-4! md:ml-2! font-black` };
  color: #f0fdf4 !important;
  &:hover {
    color: #fbbf24 !important;
  }

  img {
    ${tw`w-10 mr-3 rounded-full`}
    border: 2px solid #fbbf24;
  }
`;

export const MobileNavLinksContainer = tw.nav`flex flex-1 items-center justify-between my-0`;
export const NavToggle = styled.button`
  ${tw`lg:hidden z-40 focus:outline-none transition duration-300`}
  color: #d1fae5;
  &:hover, &:focus {
    color: #fbbf24;
  }
`;
export const MobileNavLinks = motion.custom(styled.div`
  ${tw`lg:hidden z-40 fixed top-0 inset-x-0 mx-4 my-6 p-8 border text-center rounded-lg`}
  border-color: #065f46;
  background-color: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(8px);
  color: #e2e8f0;

  ${NavLinks} {
    ${tw`flex flex-col items-center`}
  }
`);

export const DesktopNavLinks = tw.nav`
  hidden lg:flex flex-1 justify-between items-center my-0
`;

// ── Search box (top navbar) ───────────────────────────────────────────────────
const SearchWrap = styled.div`
  ${tw`relative flex items-center flex-1 max-w-md mx-8`}
  overflow: visible;
`;
const SearchBox = styled.div`
  ${tw`flex items-center w-full h-10 rounded-full px-4 transition-all duration-300`}
  background-color: rgba(6, 78, 59, 0.5);
  border: 1px solid rgba(6, 78, 59, 0.8);
  &:focus-within {
    border-color: #fbbf24;
    box-shadow: 0 0 0 2px rgba(251, 191, 36, 0.2);
  }
`;
const SearchInput = styled.input`
  ${tw`bg-transparent w-full h-full text-sm focus:outline-none`}
  color: #ecfdf5;
  &::placeholder { color: rgba(16, 185, 129, 0.6); }
`;
const StyledSearchIcon = styled(SearchIcon)`
  ${tw`w-5 h-5 ml-2 flex-shrink-0 transition-colors duration-200`}
  color: #34d399;
  ${SearchBox}:focus-within & { color: #fbbf24; }
`;


const NavbarComponent = (props) => {
  const history = useHistory();
  const location = useLocation();
  const auth = getAuth();
  const [user, setUser] = useState(null);
  const [query, setQuery] = useState("");
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);

  const tabCardGridData = props.TabCardGridData;
  const searchIndex = useMemo(() => buildSearchIndex(tabCardGridData), [tabCardGridData]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return searchIndex
      .filter((item) => {
        const contentArray = Array.isArray(item.contentArray)
          ? item.contentArray
          : (item.contentArray ? String(item.contentArray) : "");
        const haystack = [
          item.title,
          item.name,
          item.content,
          contentArray,
          item.description,
          item.queryBy,
          item.category,
          item.marketName,
          item.businessName,
        ].filter(Boolean).join(" ").toLowerCase();
        return haystack.includes(q);
      });
  }, [query, searchIndex]);

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const goToSearch = (path) => {
    setShowResults(false);
    setQuery("");
    history.push(path);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    goToSearch(`/marketplace?page=stock&tab=Foods&q=${encodeURIComponent(query.trim())}`);
  };

  const searchForm = (
    <form onSubmit={handleSearchSubmit} style={{ width: "100%" }}>
      <SearchBox>
        <SearchInput
          type="text"
          value={query}
          placeholder="Search foods, markets or categories..."
          onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
          onFocus={() => setShowResults(true)}
        />
        <StyledSearchIcon />
      </SearchBox>
    </form>
  );

  console.log("Navbar - Auth instance:", auth);
  console.log("Navbar - Current user state:", user);

  useEffect(() => {
    console.log("Navbar - Setting up auth state listener");
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log("Navbar - Auth state changed:", currentUser);
      if (currentUser) {
        console.log("Navbar - User logged in:", {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName
        });
      } else {
        console.log("Navbar - No user logged in");
      }
      setUser(currentUser);
    });
    return () => {
      console.log("Navbar - Cleaning up auth listener");
      unsubscribe();
    }
  }, [auth]);

  const handleLogout = async (e) => {
    e.preventDefault();
    console.log("Navbar - Logout clicked");
    try {
      await signOut(auth);
      console.log("Navbar - Logout successful");
      history.push('/');
    } catch (error) {
      console.error("Navbar - Logout error:", error);
    }
  };

  const roundedHeaderButton = props.NavBarData.roundedHeaderButton;
  const collapseBreakpointClass = props.NavBarData.collapseBreakpointClass;
  const defaultLinksData = props.NavBarData.defaultLinks;
  const PrimaryLinkData = props.NavBarData.PrimaryLinkData;
  const defaultLogoLinkData = props.NavBarData.defaultLogoLink;

  // Filter out login/signup links if user is logged in, and remove Home (logo handles that)
  const filteredLinks = defaultLinksData
    .filter(link => link.linkText !== 'Home')
    .filter(link => !user ? true : link.linkText !== 'Log in' && link.linkText !== 'Sign Up');

  // Check if user is admin (super-admin or sub-admin)
  const [userRole, setUserRole] = useState(null);
  const isAdmin = user && (user.email === SUPER_ADMIN_EMAIL || userRole === ROLE_SUB_ADMIN || userRole === 'super_admin');

  useEffect(() => {
    if (user) {
      if (user.email === SUPER_ADMIN_EMAIL) {
        setUserRole('super_admin');
      } else {
        // Fetch role from Firestore for other users
        fetchUserByEmail(user.email).then(firestoreUser => {
          if (firestoreUser && firestoreUser.role) {
            setUserRole(firestoreUser.role);
          }
        }).catch(err => console.error("Error fetching user role", err));
      }
    } else {
      setUserRole(null);
    }
  }, [user]);

  const isActive = (href) => {
    if (!href || href.startsWith('/#') || href === '#') return false;
    if (href === '/') return location.pathname === '/';
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const nl = filteredLinks.map((n, i) => (
    <NavLink key={i} selected={isActive(n.href)} onClick={
      (e) => {
        e.preventDefault()
        history.push(n.href)
      }
    }>{n.linkText}</NavLink>
  ));

  const defaultLinks = (
    <NavLinks key={1}>
      {filteredLinks.map((n, i) => (
        <NavLink key={i} selected={isActive(n.href)} onClick={
          (e) => {
            e.preventDefault()
            history.push(n.href)
          }
        }>{n.linkText}</NavLink>
      ))}
      {isAdmin && (
        <NavLink selected={isActive('/admin')} onClick={(e) => { e.preventDefault(); history.push('/admin'); }}>
          Admin
        </NavLink>
      )}
      {user ? (
        <PrimaryLink css={roundedHeaderButton && tw`rounded-full`} onClick={handleLogout}>
          Logout
        </PrimaryLink>
      ) : (
        <PrimaryLink css={roundedHeaderButton && tw`rounded-full`} href={PrimaryLinkData.href} onClick={(e) => {
          e.preventDefault();
          history.push(PrimaryLinkData.href);
        }}>
          {PrimaryLinkData.linkText}
        </PrimaryLink>
      )}
    </NavLinks>
  );

  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];

  const defaultLogoLink = (
    <LogoLink onClick={
      (e) => {
        e.preventDefault()
        history.push(defaultLogoLinkData.href)
      }
    } >{/*href={defaultLogoLinkData.href}*/}
      <img src={defaultLogoLinkData.logo} alt="logo" />
      {defaultLogoLinkData.linkText}
    </LogoLink>
  );

  const logoLink = defaultLogoLink;
  const links = defaultLinks;

  return (
    <>
      <Header className={"header-light"}>
        <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
          {logoLink}
          <SearchWrap ref={searchRef}>
            {searchForm}
            {showResults && (
              <SearchResultsDropdown results={results} query={query} onSelect={goToSearch} />
            )}
          </SearchWrap>
          {links}
        </DesktopNavLinks>
        <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
          {logoLink}
          <NavToggle onClick={toggleNavbar} className={showNavLinks ? "open" : "closed"}>
            {showNavLinks ? <CloseIcon tw="w-6 h-6" /> : <MenuIcon tw="w-6 h-6" />}
          </NavToggle>
          <div tw="w-full mt-2" style={{ position: "relative", flex: "1 1 100%", order: 3 }}>
            {searchForm}
            {showResults && (
              <SearchResultsDropdown results={results} query={query} onSelect={goToSearch} />
            )}
          </div>
          <MobileNavLinks initial={{ x: "150%", display: "none" }} animate={animation} css={collapseBreakpointCss.mobileNavLinks}>
            {links}
          </MobileNavLinks>
        </MobileNavLinksContainer>
      </Header>
      <Header2 />
    </>
  );
};

/* The below code is for generating dynamic break points for navbar.
 * Using this you can specify if you want to switch
 * to the toggleable mobile navbar at "sm", "md" or "lg" or "xl" above using the collapseBreakpointClass prop
 * Its written like this because we are using macros and we can not insert dynamic variables in macros
 */

const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden flex-wrap gap-2`
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden flex-wrap gap-2`
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden flex-wrap gap-2`
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden flex-wrap gap-2`
  }
};


const Navbar = connect((state) => ({
  NavBarData: state.NavBarData,
  TabCardGridData: state.TabCardGridData,
}))(NavbarComponent);

export default Navbar;
